import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin } from '@phosphor-icons/react';
import DiscoverProductCard from './DiscoverProductCard';
import { useStoreStatus } from '../../context/StoreStatusContext';

// One store's block on the home feed: a header that links into the store,
// then up to three product rows (featured / most ordered / most rated - the
// last is the backend's `mostReviewed`, ranked by number of star ratings).
//
// Rows with nothing in them are skipped rather than rendered empty - a store
// with no orders yet simply shows fewer rows. The one exception is the
// featured row, which the backend backfills with that store's top products
// when the owner hasn't curated any; `featuredIsFallback` tells us that
// happened, so the row is honestly labelled "Popular" instead of "Featured".
const StoreDiscoverSection = ({ block }) => {
  const { store, featured, mostOrdered, mostReviewed, featuredIsFallback } = block;
  const { isStoreOpen } = useStoreStatus();
  const open = isStoreOpen(store.id, store.isStoreOpen);

  const rows = [
    {
      key: 'featured',
      title: featuredIsFallback ? 'Popular here' : 'Featured',
      products: featured
    },
    { key: 'mostOrdered', title: 'Most ordered', products: mostOrdered },
    { key: 'mostReviewed', title: 'Most rated', products: mostReviewed }
  ].filter((row) => row.products && row.products.length > 0);

  if (!rows.length) return null;

  return (
    <section className="py-8 border-b border-gray-100 last:border-b-0">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{store.name}</h2>
            <span
              className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium shrink-0 ${
                open ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'
              }`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${open ? 'bg-green-600' : 'bg-gray-400'}`} />
              {open ? 'Open' : 'Closed'}
            </span>
          </div>
          {store.deliversToYou === false && (
            <p className="flex items-center gap-1.5 text-xs text-gray-400 mt-1">
              <MapPin size={13} weight="bold" /> Outside your delivery area
            </p>
          )}
        </div>

        <Link
          to={`/store/${store.id}`}
          className="shrink-0 inline-flex items-center gap-1 text-sm font-medium text-amber-600 hover:text-amber-700 transition-colors min-h-11"
        >
          Show more <ArrowRight size={16} weight="bold" />
        </Link>
      </div>

      <div className="space-y-6">
        {rows.map((row) => (
          <div key={row.key}>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{row.title}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {row.products.map((product) => (
                <DiscoverProductCard key={`${row.key}-${product.id}`} product={product} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default StoreDiscoverSection;
